import { EigenvalueDecomposition, Matrix } from 'ml-matrix';
import type { PlaneBasis, PlaneFit, RegionBounds2D, RegionGrowResult, Vec3 } from './types';
import { triCentroid } from './triangle';
import { add, cross, dot, normalize, scale, sub, vertexAt } from './vec';

const WORLD_UP: Vec3 = [0, 1, 0];

/** Collect the unique vertex indices used by a set of triangles. */
function regionVertices(indices: ArrayLike<number>, regionTris: Uint32Array): number[] {
  const set = new Set<number>();
  for (let i = 0; i < regionTris.length; i++) {
    const t = regionTris[i];
    set.add(indices[t * 3]);
    set.add(indices[t * 3 + 1]);
    set.add(indices[t * 3 + 2]);
  }
  return [...set];
}

/**
 * Orthonormal plane basis. `up` is world +Y projected onto the plane (so walls /
 * murals get a gravity-aligned, upright rect); if the plane is near-horizontal
 * (floor/ceiling) we fall back to the region's PCA major axis. The result is
 * right-handed: right × up = normal.
 */
export function computePlaneBasis(
  positions: Float32Array,
  indices: Uint32Array,
  regionTris: Uint32Array,
  plane: PlaneFit,
): PlaneBasis {
  const n = normalize(plane.normal);

  // Origin = mean of region triangle centroids.
  let ox = 0, oy = 0, oz = 0;
  for (let i = 0; i < regionTris.length; i++) {
    const [cx, cy, cz] = triCentroid(positions, indices, regionTris[i]);
    ox += cx;
    oy += cy;
    oz += cz;
  }
  const count = Math.max(1, regionTris.length);
  const origin: Vec3 = [ox / count, oy / count, oz / count];

  let right: Vec3;
  let up: Vec3;
  const upProj = sub(WORLD_UP, scale(n, dot(WORLD_UP, n))); // gravity projected onto plane
  const upLen = Math.sqrt(dot(upProj, upProj));

  if (upLen > 0.2) {
    up = normalize(upProj);
    right = normalize(cross(up, n));
    up = cross(n, right); // re-orthogonalize → exact right-handed frame
  } else {
    // Near-horizontal plane: use the region's principal in-plane direction.
    const e1 = normalize(cross(n, Math.abs(n[0]) > 0.9 ? [0, 0, 1] : [1, 0, 0]));
    const e2 = cross(n, e1);
    const verts = regionVertices(indices, regionTris);
    let saa = 0, sab = 0, sbb = 0;
    for (const v of verts) {
      const p = sub(vertexAt(positions, v), origin);
      const a = dot(p, e1);
      const b = dot(p, e2);
      saa += a * a; sab += a * b; sbb += b * b;
    }
    // Major in-plane axis = eigenvector of the largest eigenvalue of the 2×2
    // covariance, via ml-matrix (was a hand-rolled atan2 closed form).
    const evd = new EigenvalueDecomposition(
      new Matrix([
        [saa, sab],
        [sab, sbb],
      ]),
      { assumeSymmetric: true },
    );
    const ev = evd.realEigenvalues;
    const major = ev[0] >= ev[1] ? 0 : 1;
    const axis = evd.eigenvectorMatrix.getColumn(major);
    right = normalize(add(scale(e1, axis[0]), scale(e2, axis[1])));
    up = cross(n, right);
  }

  return { origin, normal: n, right, up };
}

const percentile = (sorted: number[], p: number): number => {
  if (sorted.length === 0) return 0;
  const idx = Math.max(0, Math.min(sorted.length - 1, Math.round(p * (sorted.length - 1))));
  return sorted[idx];
};

/**
 * Oriented 2D bounds of the region in plane (u,v) space, trimmed to the 2nd/98th
 * percentile per axis so a few outlier vertices don't blow the initial rect up.
 */
export function computeRegionBounds2D(
  positions: Float32Array,
  indices: Uint32Array,
  regionTris: Uint32Array,
  basis: PlaneBasis,
): RegionBounds2D {
  const verts = regionVertices(indices, regionTris);
  const us: number[] = [];
  const vs: number[] = [];
  for (const vi of verts) {
    const p = sub(vertexAt(positions, vi), basis.origin);
    us.push(dot(p, basis.right));
    vs.push(dot(p, basis.up));
  }
  us.sort((a, b) => a - b);
  vs.sort((a, b) => a - b);
  return {
    min: [percentile(us, 0.02), percentile(vs, 0.02)],
    max: [percentile(us, 0.98), percentile(vs, 0.98)],
  };
}

/**
 * Per-vertex wave depths for the reveal FX, split per source mesh. Each ORIGINAL
 * vertex gets the minimum BFS depth of any accepted triangle touching it (via the
 * weld remap), or -1 if it is outside the region. Feed each array into the source
 * mesh's `ejDepth` vertex attribute.
 */
export function computeVertexWaveDepths(
  result: RegionGrowResult,
  weldedIndices: Uint32Array,
  remap: Uint32Array, // merged-original vertex -> welded vertex
  meshVertexOffset: Uint32Array, // mesh ordinal -> first merged-original vertex
  totalOriginalVertices: number,
  weldedCount: number,
): Float32Array[] {
  const weldedDepth = new Float32Array(weldedCount).fill(Infinity);
  for (let i = 0; i < result.triangles.length; i++) {
    const t = result.triangles[i];
    const d = result.depths[i];
    for (let k = 0; k < 3; k++) {
      const wv = weldedIndices[t * 3 + k];
      if (d < weldedDepth[wv]) weldedDepth[wv] = d;
    }
  }

  const meshCount = meshVertexOffset.length;
  const out: Float32Array[] = [];
  for (let m = 0; m < meshCount; m++) {
    const start = meshVertexOffset[m];
    const end = m + 1 < meshCount ? meshVertexOffset[m + 1] : totalOriginalVertices;
    const arr = new Float32Array(end - start);
    for (let v = start; v < end; v++) {
      const depth = weldedDepth[remap[v]];
      arr[v - start] = Number.isFinite(depth) ? depth : -1;
    }
    out.push(arr);
  }
  return out;
}
